import { OmniaPromise } from "./promises"
import { showWarning } from "./shell/msgbox/msgbox"
import { showException } from "./shell/msgbox/msgbox"
import { IMessageBox } from "./shell/types"
import { str } from "./strings"
import { isArray, isObject, isString } from "./utils/utils"

export interface OmniaErrorHandler {

    readonly sessionId: string
    readonly userId: number

    showMessageBox(options: IMessageBox): OmniaPromise<any>

}

export interface HandleApiErrorOptions {
    resourceType?: string
    deleting?: boolean
    defaultMessage?: string
    conflict?: "retry" | "stop"
    formatApiError?: (error: OmniaApiError) => string
}

export interface OmniaApiException {
    ApiException: true
    Message: string
}


export interface OmniaApiError {
    Message: string
    ErrorId?: string
    Data?: any
}

export interface OmniaConcurrencyError<T = any> {
    Resources: string[]
    Locks: {
        ResourceId: string
        SessionGuid: string
        UserId: number
        UserName: string
        UserEmail: string
    }[]
    Content: T
}

const msgUnexpected = "An unexpected error has occurred"

export function catchApiError(sh: OmniaErrorHandler, run: () => ng.IPromise<any>) {
    return run().catch(e => handleApiError(sh, e))
}

export function handleApiError(sh: OmniaErrorHandler, error: any, options?: HandleApiErrorOptions) {

    console.error(error, options)

    if (error && isString(error)) {
        const content = `<p style='white-space: pre-wrap'>${error}</p>`
        return showWarning(sh, content, str.Error)
    }

    if (!isObject(error)) {
        return showException(sh, undefined, msgUnexpected);
    }

    if (isString(error.data) && Boolean(error.data)) {
        const content = `<p style='white-space: pre-wrap;'>${error.data}</p>`
        return showWarning(sh, content, str.Error)
    }

    options = {
        deleting: false,
        conflict: "retry",
        ...options
    }

    const { resourceType, deleting, defaultMessage, formatApiError } = options

    const concurrencyError = getApiConcurrencyError(error)

    if (concurrencyError) {
        return handleConcurrencyError(sh, concurrencyError, options)
    }

    const apiError = getApiError(error)

    if (apiError) {

        let msg = formatApiError ? formatApiError(apiError) : undefined

        function getDefault() {
            const msgUpdate = `<p>This ${resourceType} could not be updated</p>`
            const msgDelete = `<p>This ${resourceType} could not be deleted</p>`
            return deleting ? msgDelete : msgUpdate
        }

        msg = msg || (
            apiError.Message && resourceType
                ? `${getDefault()}<p>${apiError.Message}</p>`
                : apiError.Message
                    ? `<p>${apiError.Message}</p>`
                    : defaultMessage
                        ? defaultMessage
                        : resourceType
                            ? getDefault()
                            : getDefaultApiExceptionMessage(deleting, resourceType)
        )

        return showWarning(sh, msg, str.Error)

    }

    const data = error.data
    const msg = defaultMessage || getDefaultApiExceptionMessage(deleting, resourceType)

    return showException(sh, data, msg);

}


export function getConcurrencyErrorMessage(sh: OmniaErrorHandler, err: OmniaConcurrencyError, { conflict, defaultMessage }: HandleApiErrorOptions) {

    const msgAnotherUser = "another user"
    const title = "Conflict"

    conflict = conflict || "retry"

    const { sessionId, userId } = sh

    const info = getConflictInfo(sessionId, err)
    const self = info && info.UserId == userId

    let msg = defaultMessage ?? getDefaultConcurrencyMsg(conflict, self)

    msg = msg.replace("{{user}}", info?.UserName ? info.UserName : msgAnotherUser)

    return { title, message: msg }

}

function getDefaultConcurrencyMsg(conflict: "retry" | "stop", self: boolean) {

    const msgStopSelf = `<p>This action cannot be completed now because you are currently performing a conflicting operation on the same holding.</p>
        <p>Please check if you have other tabs open on the same holding.</p>`

    const msgStopOtherUser = `<p>This action cannot be completed now because {{user}} is currently performing a conflicting operation on the same holding.</p>
        <p>Please try again later.</p>`


    const msgRetry = `<p>Another user is currently making conflicting changes on this holding.</p>
                <p>Please retry later, but be aware that this may then overwrite changes made by this user.</p>
                <p>We advise that you first refresh the data by reloading the page.`

    switch (conflict) {

        case "stop":
            return self ? msgStopSelf : msgStopOtherUser


        default:
            return msgRetry


    }
}

export function handleConcurrencyError(sh: OmniaErrorHandler, err: OmniaConcurrencyError, options: HandleApiErrorOptions) {
    const { title, message } = getConcurrencyErrorMessage(sh, err, options)
    return showWarning(sh, message, title).then(() => false)
}

export function handleException(sh: OmniaErrorHandler, error: any, msg?: string) {
    const options: HandleApiErrorOptions = msg ? { defaultMessage: msg } : undefined
    return handleApiError(sh, error, options)
}


function getConflictInfo(sessionId: string, err: OmniaConcurrencyError) {

    const locks = (err.Locks || []).filter(l => (l.SessionGuid != sessionId))

    return locks[0]

}

function getDefaultApiExceptionMessage(deleting: boolean, resourceType: string) {

    const msgErrorUpdating = `<p>An error has occurred while updating this ${resourceType}. The changes have not been saved.</p><p>Please review the changes to check for potential errors, and try saving again.</p>`
    const msgErrorDeleting = `<p>An error has occurred while deleting this ${resourceType}.</p><p>Please try again later.</p>`

    return resourceType && (!deleting)
        ? msgErrorUpdating
        : resourceType
            ? msgErrorDeleting
            : msgUnexpected


}

export function isApiConcurrencyError(r: { status: number, data: any }): r is { status: number, data: OmniaConcurrencyError } {

    if (r && (r.status == 409) && isObject(r.data) && isArray(r.data.Resources)) {
        return true
    }

}

export function getApiConcurrencyError(r: { status: number, data: any }): OmniaConcurrencyError {

    if (isApiConcurrencyError(r)) {
        return r.data
    }

}

function getApiError(r: { status: number, data: any }): OmniaApiError {

    if (r && (r.status >= 400) && (r.status < 500)) {

        const e = toPascalCase(r.data)

        if (isObject(e)) {

            const errors = e["Errors"]

            if ((isArray(errors)) && (errors.length > 0)) {
                return errors[0]
            }

        }

    }

}

export function isApiException(e: any): e is OmniaApiException {
    return e && e.ApiException && e.Message
}

function toPascalCase(i: any) {

    if (i === undefined) {
        return i
    }

    if (isArray(i)) {
        return i.map(toPascalCase)
    }

    if (!isObject(i)) {
        return i
    }

    const r = {}

    for (const key in i) {
        const pc = key[0].toUpperCase() + key.slice(1)
        r[pc] = toPascalCase(i[key])
    }

    return r

}
